import { useState, useEffect } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { fetchExperimentDataForTournamentAsync } from "@/api/tournaments";
import { loadSvcContentTagsAsync } from "@/api/resources";
import type { CatalogTagOption } from "sj-ui-toolkit/src/client/svcContent";

interface FilterData {
  tagsData: Record<string, CatalogTagOption[]>;
  experimentLanguagesList: Array<{ name: string }>;
  experimentDifficultiesList: Array<{ name: string }>;
}

export function useTournamentFilterData(currentTenantName: string | undefined): {
  filterData: FilterData;
  isLoading: boolean;
} {
  // Tenant/bundle-aware tags come from svc-content, gated behind this flag (see
  // PathContentStep.tsx for the same pattern) so ineligible types never appear as options.
  const { dynamicCatalogTags } = useFlags();
  const [filterData, setFilterData] = useState<FilterData>({
    tagsData: {},
    experimentLanguagesList: [],
    experimentDifficultiesList: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!currentTenantName || dynamicCatalogTags === undefined) return;

    let cancelled = false;

    const loadFilterData = async () => {
      setIsLoading(true);
      try {
        const [svcContentTags, experimentData] = await Promise.all([
          loadSvcContentTagsAsync(!!dynamicCatalogTags),
          fetchExperimentDataForTournamentAsync(),
        ]);
        if (cancelled) return;
        setFilterData({
          tagsData: svcContentTags,
          experimentLanguagesList: experimentData.languages ?? [],
          experimentDifficultiesList: experimentData.difficulties ?? [],
        });
      } catch (error) {
        console.error("Error fetching filter data:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadFilterData();
    return () => {
      cancelled = true;
    };
  }, [currentTenantName, dynamicCatalogTags]);

  return { filterData, isLoading };
}
